
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Calendar as CalendarIcon, Clock, User, Loader, CheckCircle, AlertCircle, Plus, X, Tag, ChevronLeft, ChevronRight, CreditCard, ShieldCheck, Info, MapPin, Check, Mail, Phone } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { API_BASE, STRIPE_PUBLIC_KEY } from '../config';

import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardNumberElement, CardExpiryElement, CardCvcElement, useStripe, useElements } from '@stripe/react-stripe-js';

const stripePromise = loadStripe(STRIPE_PUBLIC_KEY);

const StripePaymentForm = ({ onSubmit, t, loading, depositAmount, disabled, missingFields = [], customerInfo, acceptedTerms }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState(null);
  const [cardStates, setCardStates] = useState({
    number: { complete: false },
    expiry: { complete: false },
    cvc: { complete: false }
  });
  const [postalCode, setPostalCode] = useState('');

  const isDemo = STRIPE_PUBLIC_KEY.includes('****************');

  const elementStyle = {
    style: {
      base: {
        color: "#1e293b",
        fontFamily: 'Inter, system-ui, sans-serif',
        fontSmoothing: "antialiased",
        fontSize: "16px",
        "::placeholder": { color: "#94a3b8" }
      },
      invalid: { color: "#ef4444", iconColor: "#ef4444" }
    }
  };

  const handleElementChange = (name, event) => {
    setCardStates(prev => ({
      ...prev,
      [name]: { complete: event.complete, error: event.error?.message }
    }));
    if (event.error) setError(event.error.message);
    else setError(null);
  };

  const isCardComplete = cardStates.number.complete && cardStates.expiry.complete && cardStates.cvc.complete && postalCode.trim().length >= 2;
  const isActuallyDisabled = disabled || loading || !acceptedTerms || (!isDemo && !isCardComplete);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isActuallyDisabled) return;
    
    if (isDemo) {
        onSubmit("demo_payment_id_" + Date.now());
        return;
    }

    if (!stripe || !elements) return;
    setError(null);
    const cardNumberElement = elements.getElement(CardNumberElement);

    try {
        const res = await fetch(`${API_BASE}/bookings/create-payment-intent`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ amount: depositAmount * 100 })
        });
        const data = await res.json();
        
        if (data.error) { 
            setError(data.error); 
            return; 
        }

        const result = await stripe.confirmCardPayment(data.clientSecret, {
            payment_method: { 
                card: cardNumberElement,
                billing_details: {
                    name: customerInfo.name,
                    email: customerInfo.email,
                    phone: customerInfo.phone,
                    address: {
                        line1: customerInfo.address,
                        postal_code: postalCode
                    }
                }
            }
        });

        if (result.error) {
            setError(result.error.message);
        } else {
            if (result.paymentIntent.status === 'requires_capture' || result.paymentIntent.status === 'succeeded') {
                onSubmit(result.paymentIntent.id);
            }
        }
    } catch (e) { 
        setError(t?.booking?.errors?.server || "Erreur de communication avec le serveur."); 
    }
  };

  return (
    <div className="space-y-6">
      <div className={`bg-white border-2 p-5 rounded-2xl transition-all ${disabled ? 'border-slate-100 opacity-50' : 'border-brand-100 shadow-sm'}`}>
        <label className="text-[11px] font-black uppercase tracking-widest text-slate-800 flex items-center mb-4">
          <CreditCard className="h-4 w-4 mr-2 text-brand-600" /> 
          {t?.booking?.deposit?.bankGuarantee} <span className="text-red-500 ml-1">*</span>
        </label>
        
        <div className="space-y-3">
          <div className="p-3 border border-slate-200 rounded-xl bg-slate-50/30 focus-within:border-brand-400 focus-within:ring-1 focus-within:ring-brand-400 transition-all">
            <CardNumberElement options={elementStyle} onChange={(e) => handleElementChange('number', e)} />
          </div>
          
          <div className="p-3 border border-slate-200 rounded-xl bg-slate-50/30 focus-within:border-brand-400 transition-all">
            <CardExpiryElement options={elementStyle} onChange={(e) => handleElementChange('expiry', e)} />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 border border-slate-200 rounded-xl bg-slate-50/30 focus-within:border-brand-400 transition-all">
              <CardCvcElement options={elementStyle} onChange={(e) => handleElementChange('cvc', e)} />
            </div>
            <div className="px-3 border border-slate-200 rounded-xl bg-slate-50/30 focus-within:border-brand-400 transition-all flex items-center">
              <input 
                type="text" 
                placeholder={t?.booking?.placeholders?.zip} 
                value={postalCode}
                onChange={(e) => setPostalCode(e.target.value)}
                disabled={disabled}
                className="w-full py-1 outline-none bg-transparent text-slate-800 placeholder-slate-400 text-sm font-medium"
              />
            </div>
          </div>
        </div>

        {error && <div className="mt-3 text-red-600 text-[11px] font-bold flex items-center bg-red-50 p-2 rounded-lg border border-red-100"><AlertCircle className="h-3 w-3 mr-2" /> {error}</div>}
      </div>

      <button 
        type="button" 
        onClick={handleSubmit} 
        disabled={isActuallyDisabled} 
        className={`w-full py-4 rounded-2xl font-black text-sm transition-all flex justify-center items-center transform active:scale-95 shadow-xl uppercase tracking-widest border-b-4 ${isActuallyDisabled ? 'bg-slate-300 text-slate-500 border-slate-400 cursor-not-allowed shadow-none' : 'bg-brand-600 text-white border-brand-800 hover:bg-brand-700 hover:scale-[1.02]'}`}
      >
        {loading ? <Loader className="animate-spin h-5 w-5 text-white" /> : t?.booking?.submit}
      </button>

      {!isActuallyDisabled ? (
          <p className="text-[10px] text-center text-slate-400 font-medium px-4">{t?.booking?.deposit?.disclaimer}</p>
      ) : (
          <div className="bg-amber-50 p-3 rounded-xl border border-amber-100">
             <p className="text-[9px] text-amber-800 font-black uppercase text-center leading-tight">
                {t?.booking?.deposit?.completeFormError}
             </p>
          </div>
      )}
    </div>
  );
};

export default function BookingForm({ t, lang }) {
  const location = useLocation();
  const navigate = useNavigate();
  const DEPOSIT_AMOUNT = 20;
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', date: '', time: '', address: '', notes: '' });
  const [selectedServices, setSelectedServices] = useState([]);
  const [services, setServices] = useState([]);
  const [status, setStatus] = useState('idle'); 
  const [bookedSlots, setBookedSlots] = useState([]);
  const [allBookings, setAllBookings] = useState([]); 
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [viewDate, setViewDate] = useState(new Date());

  const getName = useCallback((s) => s[`name_${lang}`] || s.name_fr || s.name, [lang]);
  const totalPrice = useMemo(() => selectedServices.reduce((acc, sName) => acc + Number(services.find(s => getName(s) === sName)?.price || 0), 0), [selectedServices, services, getName]);

  useEffect(() => { 
    fetch(`${API_BASE}/services`).then(res => res.json()).then(data => data && setServices(data)); 
    fetch(`${API_BASE}/bookings`).then(res => res.json()).then(data => setAllBookings(data || []));
  }, []);

  useEffect(() => { if (location.state?.service && !selectedServices.includes(location.state.service)) setSelectedServices([location.state.service]); }, [location.state, selectedServices]);
  useEffect(() => { if (formData.date) { fetch(`${API_BASE}/bookings/availability?date=${formData.date}`).then(res => res.json()).then(data => setBookedSlots(Array.isArray(data) ? data.map(t => String(t).substring(0, 5)) : [])); } }, [formData.date]);

  const bookedDatesSet = useMemo(() => new Set(allBookings.map(b => b.booking_date)), [allBookings]);

  const handleBookingConfirm = async (stripeId) => {
    setStatus('submitting');
    try {
      const res = await fetch(`${API_BASE}/bookings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, services: selectedServices, language: lang, deposit_amount: DEPOSIT_AMOUNT, stripe_payment_id: stripeId }),
      });
      if (!res.ok) throw new Error();
      setStatus('success');
    } catch { setStatus('error'); }
  };

  const missingFields = useMemo(() => {
    const missing = [];
    const v = t?.booking?.validation || {};
    if (selectedServices.length === 0 || selectedServices.every(s => !s)) missing.push(v.service);
    if (!formData.date) missing.push(v.date);
    if (!formData.time) missing.push(v.time);
    if (!formData.name || !formData.name.trim()) missing.push(v.name);
    if (!formData.email || !formData.email.trim()) missing.push(v.email);
    if (!formData.address || !formData.address.trim()) missing.push(v.address);
    return missing;
  }, [formData, selectedServices, t]);

  const isFormComplete = missingFields.length === 0;

  if (status === 'success') {
    return (
      <div className="max-w-4xl mx-auto px-4 py-16">
        <div className="bg-white rounded-3xl shadow-2xl p-12 text-center border border-slate-100 animate-fade-in-up">
          <div className="inline-flex items-center justify-center w-24 h-24 bg-green-100 rounded-full mb-8"><CheckCircle className="h-12 w-12 text-green-600" /></div>
          <h3 className="text-4xl font-extrabold text-slate-800 mb-4">{t?.booking?.success?.title}</h3>
          <p className="text-slate-600 text-xl mb-12 max-w-2xl mx-auto leading-relaxed" dangerouslySetInnerHTML={{ __html: t?.booking?.success?.body }}></p>
          <button onClick={() => navigate('/')} className="px-10 py-4 bg-brand-600 text-white rounded-full font-bold shadow-lg transform active:scale-95 transition-all">{t?.booking?.success?.returnHome}</button>
        </div>
      </div>
    );
  }

  const calendarData = (() => {
    const year = viewDate.getFullYear(), month = viewDate.getMonth(), daysInMonth = new Date(year, month + 1, 0).getDate();
    const firstDay = new Date(year, month, 1).getDay(), adjustedFirstDay = firstDay === 0 ? 6 : firstDay - 1, days = [];
    for (let i = 0; i < adjustedFirstDay; i++) days.push(null);
    for (let i = 1; i <= daysInMonth; i++) {
        const d = new Date(year, month, i), dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(i).padStart(2, '0')}`;
        const today = new Date(); today.setHours(0,0,0,0);
        days.push({ day: i, dateStr, isPast: d < today, isToday: d.toDateString() === new Date().toDateString() });
    }
    return days;
  })();

  return (
    <div className="max-w-7xl mx-auto px-4 py-12">
      <div className="bg-white rounded-[3rem] shadow-2xl overflow-hidden border border-slate-100">
        <div className="bg-slate-900 px-8 py-10 text-center relative overflow-hidden">
            <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-brand-500 via-transparent to-transparent"></div>
            <h2 className="text-3xl md:text-4xl font-black text-white flex items-center justify-center relative z-10 uppercase tracking-tighter">
              {t?.booking?.title}
            </h2>
        </div>

        <div className="grid lg:grid-cols-12 bg-slate-50/40">
            
            {/* Colonne Gauche: Formulaire Principal */}
            <div className="lg:col-span-8 p-6 md:p-12 space-y-10 border-r border-slate-100">
                
                {/* 1. Services */}
                <section className="bg-white p-8 rounded-3xl shadow-sm border border-slate-100">
                    <h3 className="text-sm font-black text-slate-400 mb-6 flex items-center uppercase tracking-[0.2em]">
                      <Tag className="h-4 w-4 mr-3 text-brand-600" /> {t?.booking?.steps?.services}
                    </h3>
                    <div className="space-y-4">
                        {selectedServices.map((cs, idx) => (
                            <div key={idx} className="flex gap-3 animate-fade-in">
                                <select required value={cs} onChange={(e) => { const n = [...selectedServices]; e.target.value ? n[idx] = e.target.value : n.splice(idx,1); setSelectedServices(n); }} className="flex-1 px-5 py-4 border-2 border-slate-100 rounded-2xl bg-slate-50/30 outline-none focus:bg-white focus:border-brand-500 font-bold text-slate-700 transition-all text-sm">
                                    <option value="">{t?.booking?.selectService}</option>
                                    {services.map(s => <option key={s.id} value={getName(s)}>{getName(s)} — {s.price}€</option>)}
                                </select>
                                <button onClick={() => { const n = [...selectedServices]; n.splice(idx,1); setSelectedServices(n); }} className="p-4 text-slate-300 hover:text-red-500 transition-all"><X/></button>
                            </div>
                        ))}
                        <button onClick={() => setSelectedServices([...selectedServices, ''])} className="text-brand-700 font-black flex items-center gap-2 px-5 py-3 hover:bg-brand-50 rounded-2xl transition-all border-2 border-dashed border-brand-200 text-xs uppercase tracking-widest">
                            <Plus className="h-4 w-4" /> {t?.booking?.addService}
                        </button>
                    </div>
                </section>

                {/* 2 & 3. Date & Heure */}
                <div className="grid md:grid-cols-2 gap-8">
                    <section className="bg-white p-8 rounded-3xl shadow-sm border border-slate-100">
                        <h3 className="text-sm font-black text-slate-400 mb-6 flex items-center uppercase tracking-[0.2em]">
                          <CalendarIcon className="h-4 w-4 mr-3 text-brand-600" /> {t?.booking?.steps?.date}
                        </h3>
                        <div className="border border-slate-100 rounded-2xl overflow-hidden">
                            <div className="flex justify-between p-3 bg-slate-50 border-b border-slate-100">
                                <span className="font-black text-[10px] text-slate-800 uppercase tracking-widest">{viewDate.toLocaleDateString(lang, {month: 'long', year: 'numeric'})}</span>
                                <div className="flex gap-1">
                                    <button onClick={() => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth()-1, 1))} className="p-1 hover:bg-white rounded transition-all"><ChevronLeft className="h-3 w-3"/></button>
                                    <button onClick={() => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth()+1, 1))} className="p-1 hover:bg-white rounded transition-all"><ChevronRight className="h-3 w-3"/></button>
                                </div>
                            </div>
                            <div className="grid grid-cols-7 p-2 text-center text-[10px] font-black">
                                {['L','M','M','J','V','S','D'].map(d=><div key={d} className="py-2 text-slate-300">{d}</div>)}
                                {calendarData.map((d,i)=>d ? (
                                    <button key={i} disabled={d.isPast} onClick={()=>setFormData({...formData, date: d.dateStr})} className={`p-2 rounded-lg transition-all m-0.5 relative ${formData.date===d.dateStr ? 'bg-brand-600 text-white shadow-md' : d.isPast ? 'text-slate-200 cursor-not-allowed' : 'text-slate-600 hover:bg-brand-50'}`}>
                                        {d.day}
                                        {bookedDatesSet.has(d.dateStr) && <span className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 bg-brand-400 rounded-full"></span>}
                                    </button>
                                ) : <div key={i}></div>)}
                            </div>
                        </div>
                    </section>

                    <section className="bg-white p-8 rounded-3xl shadow-sm border border-slate-100">
                        <h3 className="text-sm font-black text-slate-400 mb-6 flex items-center uppercase tracking-[0.2em]">
                          <Clock className="h-4 w-4 mr-3 text-brand-600" /> {t?.booking?.steps?.time}
                        </h3>
                        {formData.date ? (
                            <div className="grid grid-cols-2 gap-2 max-h-[220px] overflow-y-auto custom-scrollbar pr-2">
                                {['09:00','10:00','11:00','12:00','13:00','14:00','15:00','16:00','17:00','18:00','19:00'].map(slot => (
                                    <button key={slot} disabled={bookedSlots.includes(slot)} onClick={() => setFormData({...formData, time: slot})} className={`py-3 px-2 border-2 rounded-xl font-black text-xs transition-all ${formData.time === slot ? 'border-brand-600 bg-brand-50 text-brand-700' : bookedSlots.includes(slot) ? 'opacity-20 cursor-not-allowed bg-slate-50 border-transparent' : 'border-slate-50 text-slate-500 hover:border-brand-200'}`}>{slot}</button>
                                ))}
                            </div>
                        ) : (
                            <div className="h-full flex flex-col items-center justify-center text-slate-300 italic text-[10px] uppercase font-black">
                                <CalendarIcon className="h-8 w-8 mb-2 opacity-20" />
                                {t?.booking?.selectDateFirst}
                            </div>
                        )}
                    </section>
                </div>

                {/* 4. Coordonnées */}
                <section className="bg-white p-8 rounded-3xl shadow-sm border border-slate-100">
                    <h3 className="text-sm font-black text-slate-400 mb-8 flex items-center uppercase tracking-[0.2em]">
                      <User className="h-4 w-4 mr-3 text-brand-600" /> {t?.booking?.steps?.personal}
                    </h3>
                    <div className="grid md:grid-cols-2 gap-5">
                        <input type="text" placeholder={t?.booking?.validation?.name} className="w-full px-5 py-4 bg-slate-50/50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-brand-500 outline-none font-bold text-sm" value={formData.name} onChange={e=>setFormData({...formData, name:e.target.value})} />
                        <input type="email" placeholder={t?.booking?.validation?.email} className="w-full px-5 py-4 bg-slate-50/50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-brand-500 outline-none font-bold text-sm" value={formData.email} onChange={e=>setFormData({...formData, email:e.target.value})} />
                        <input type="tel" placeholder={t?.booking?.phone} className="w-full px-5 py-4 bg-slate-50/50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-brand-500 outline-none font-bold text-sm" value={formData.phone} onChange={e=>setFormData({...formData, phone:e.target.value})} />
                        <input type="text" placeholder={t?.booking?.validation?.address} className="w-full px-5 py-4 bg-slate-50/50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-brand-500 outline-none font-bold text-sm" value={formData.address} onChange={e=>setFormData({...formData, address:e.target.value})} />
                    </div>
                    <textarea placeholder={t?.booking?.placeholders?.notes} className="w-full mt-5 px-5 py-4 bg-slate-50/50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-brand-500 outline-none resize-none font-bold text-sm" rows="2" value={formData.notes} onChange={e=>setFormData({...formData, notes:e.target.value})} />
                </section>
            </div>

            {/* Colonne Droite: Partie 5 */}
            <div className="lg:col-span-4 p-6 md:p-12 bg-slate-50/80">
                <div className="sticky top-24 space-y-8">
                    <h3 className="text-sm font-black text-slate-400 flex items-center uppercase tracking-[0.2em]">
                      <CreditCard className="h-4 w-4 mr-3 text-brand-600" /> {t?.booking?.steps?.summary}
                    </h3>

                    <div className="bg-white p-8 rounded-3xl border border-slate-100 shadow-xl relative overflow-hidden group">
                        <div className="absolute top-0 right-0 w-24 h-24 bg-brand-50 rounded-full -mr-12 -mt-12 group-hover:scale-110 transition-transform"></div>
                        <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1">{t?.booking?.summary?.estimated}</span>
                        <div className="flex items-baseline gap-2 relative z-10">
                            <span className="text-5xl font-black text-slate-900">{totalPrice.toFixed(2)}</span>
                            <span className="text-xl font-bold text-slate-400">€</span>
                        </div>
                        <p className="mt-4 text-[10px] font-bold text-brand-600 uppercase bg-brand-50 inline-block px-3 py-1 rounded-full border border-brand-100">{t?.booking?.summary?.onPlace}</p>
                    </div>

                    <div className="space-y-4">
                        <div className="bg-white/50 p-6 rounded-2xl border border-slate-200 space-y-3">
                            <div className="flex items-center text-xs font-black text-slate-800 uppercase tracking-tight">
                                <ShieldCheck className="h-4 w-4 mr-2 text-brand-600" /> {t?.booking?.deposit?.conditionsLabel}
                            </div>
                            <ul className="text-[11px] text-slate-600 space-y-2 font-medium leading-relaxed">
                                <li className="flex gap-2"><span>•</span> <span>{t?.booking?.deposit?.policy1}</span></li>
                                <li className="flex gap-2"><span>•</span> <span>{t?.booking?.deposit?.policy2}</span></li>
                            </ul>
                            
                            <div 
                              onClick={() => setAcceptedTerms(!acceptedTerms)}
                              className={`flex items-start mt-4 cursor-pointer group p-3 rounded-xl border-2 transition-all select-none ${acceptedTerms ? 'bg-brand-50 border-brand-400' : 'bg-white border-slate-200 hover:border-brand-200'}`}
                            >
                                <div className={`h-5 w-5 min-w-[20px] rounded-md border-2 flex items-center justify-center transition-all ${acceptedTerms ? 'bg-brand-600 border-brand-600' : 'bg-white border-slate-300'}`}>
                                    {acceptedTerms && <Check className="w-3 h-3 text-white" strokeWidth={4} />}
                                </div>
                                <span className={`ml-3 text-[10px] font-black uppercase tracking-tighter leading-tight ${acceptedTerms ? 'text-brand-900' : 'text-slate-500'}`}>
                                  {t?.booking?.deposit?.accept}
                                </span>
                            </div>
                        </div>

                        <div className="animate-fade-in-up">
                            <Elements stripe={stripePromise}>
                                <StripePaymentForm 
                                    depositAmount={DEPOSIT_AMOUNT} 
                                    loading={status==='submitting'} 
                                    onSubmit={handleBookingConfirm} 
                                    t={t}
                                    disabled={!isFormComplete}
                                    missingFields={missingFields}
                                    customerInfo={formData}
                                    acceptedTerms={acceptedTerms}
                                />
                            </Elements>
                        </div>
                    </div>
                </div>
            </div>
        </div>
      </div>
    </div>
  );
}
